import { FC } from 'react';
import { WidgetEmptyStateProps } from '@ui/widget-empty-state/types';
import { useTranslation } from '@modules/translation';
import { InfoRing, CloseCancelX, Checkmark, OutArrow } from '@fcc/icons';
import { P, RoundIcon, Spinner, Whitespace, Button, Flex } from '@fcc/ui';
import { WIDGET_TYPE } from '@constants/common';
import { Wrapper, CustomLink } from './styles';

export const WidgetEmptyState: FC<WidgetEmptyStateProps> = ({
    design,
    action,
    merchantName,
    amount,
    isSubscription,
    widgetType,
    paymentLink,
}) => {
    const { translate } = useTranslation();

    const isCharity = widgetType && widgetType === WIDGET_TYPE.CHARITY;
    const amountToString = amount && amount.toString();

    return (
        <Wrapper>
            {design === 'default' && (
                <>
                    <RoundIcon color="dubai10">
                        <InfoRing />
                    </RoundIcon>
                    <Whitespace pb="s4" />
                    <P weight="bold">{translate('failedToDisplayForm')}</P>
                    <Whitespace pb="s2" />
                    <P size="s" color="seattle100">
                        {translate('failedToDisplayFormDescription')}
                    </P>
                </>
            )}
            {design === 'error' && (
                <>
                    <RoundIcon color="nice10">
                        <CloseCancelX />
                    </RoundIcon>
                    <Whitespace pb="s4" />
                    <P weight="bold">{translate('failedToMakeTransaction')}</P>
                    <Whitespace pb="s2" />
                    <P size="s" color="seattle100">
                        {translate('TryAgainOrBackLater')}
                    </P>
                    {action && (
                        <>
                            <Whitespace pb="s4" />
                            <Button design="outline" onClick={action} size="s">
                                {translate('good')}
                            </Button>
                        </>
                    )}
                </>
            )}
            {design === 'waiting' && (
                <>
                    <Spinner size="l" color="london120" />
                    <Whitespace pb="s4" />
                    <P weight="bold">{translate('waitingForPayment')}</P>
                    {merchantName && (
                        <P size="s" color="seattle100">
                            {merchantName}
                        </P>
                    )}
                    {action && (
                        <>
                            <Whitespace pb="s4" />
                            <Flex direction="column" alignItems="center">
                                <Button design="outline" onClick={action} size="s" grid={3}>
                                    {translate('cancel')}
                                </Button>
                                {paymentLink && (
                                    <>
                                        <Whitespace pb="s4" />
                                        <CustomLink design="accent" target="_blank" href={paymentLink} size="m">
                                            {translate('proceedToPayment')}
                                            <OutArrow />
                                        </CustomLink>
                                    </>
                                )}
                            </Flex>
                        </>
                    )}
                </>
            )}
            {design === 'success' && (
                <>
                    <RoundIcon color="miami10">
                        <Checkmark />
                    </RoundIcon>
                    <Whitespace pb="s4" />
                    {amountToString && (
                        <P weight="bold">
                            {isCharity
                                ? translate('transferred', [amountToString])
                                : translate('paid', [amountToString])}
                        </P>
                    )}
                    <Whitespace pb="s2" />
                    {isSubscription && (
                        <P size="s" color="seattle100">
                            {translate('successSubscriptionDescription')}
                        </P>
                    )}
                    <P size="s" color="seattle100">
                        {isCharity && translate('successPaymentDescription')}
                        {!isCharity && merchantName}
                    </P>
                    {action && (
                        <>
                            <Whitespace pb="s4" />
                            <Button design="outline" onClick={action} size="s">
                                {translate('good')}
                            </Button>
                        </>
                    )}
                </>
            )}
            {design === 'loading' && <Spinner size="l" color="london120" />}
        </Wrapper>
    );
};
